import { useState, useRef } from 'react';
import { FORECAST_CALCULATION_CONSTANTS } from '@/constants/forecastCalculation';

interface ProgressState {
  progress: number;
  text: string;
}

interface UseProgressTrackingReturn {
  calculationProgress: number;
  progressText: string;
  createProgressCallback: () => (progress: number, stage: string) => Promise<void>;
  resetProgress: () => void;
}

export const useProgressTracking = (): UseProgressTrackingReturn => {
  const [calculationProgress, setCalculationProgress] = useState(0);
  const [progressText, setProgressText] = useState('');

  const progressRef = useRef<ProgressState>({ progress: 0, text: '' });
  const lastProgressUpdate = useRef(0);

  const createProgressCallback = () => {
    return async (progress: number, stage: string) => {
      const now = Date.now();

      // Always update the ref immediately
      progressRef.current = { progress, text: stage };

      // Throttle actual state updates to prevent UI overwhelming
      if (now - lastProgressUpdate.current >= FORECAST_CALCULATION_CONSTANTS.PROGRESS_UPDATE_INTERVAL || progress === 100) {
        // Use requestAnimationFrame for smooth updates
        await new Promise<void>(resolve => {
          requestAnimationFrame(() => {
            setCalculationProgress(progress);
            setProgressText(stage);
            lastProgressUpdate.current = now;
            resolve();
          });
        });

        // Small yield to allow UI to update
        await new Promise(resolve => setTimeout(resolve, 0));
      }
    };
  };

  const resetProgress = () => {
    setCalculationProgress(0);
    setProgressText('');
    progressRef.current = { progress: 0, text: '' };
    lastProgressUpdate.current = 0;
  };

  return {
    calculationProgress,
    progressText,
    createProgressCallback,
    resetProgress
  };
};